import { createFileRoute, Link } from "@tanstack/react-router";

import { breadcrumbJsonLd, seo } from "@/lib/seo";
import { Section } from "@/components/Section";
import { Container } from "@/components/Container";
import { Eyebrow } from "@/components/Eyebrow";
import { Aurora } from "@/components/Aurora";
import { PagePlaceholder } from "@/components/site/PagePlaceholder";
import { CategoryChips, PostList } from "@/components/blog/PostList";
import { getPosts } from "@/content/posts";

export const Route = createFileRoute("/_site/blog/")({
  head: () =>
    seo({
      title: "AI for Small Business: Articles",
      description:
        "Practical articles on finding where AI pays in a small business — what to automate first, when you are ready, and how to make a new system stick.",
      path: "/blog",
      jsonLd: breadcrumbJsonLd([{ name: "Articles", path: "/blog" }]),
    }),
  component: Page,
  errorComponent: () => (
    <PagePlaceholder title="This page didn't load" intro="Try refreshing the page." />
  ),
});

function Page() {
  const posts = getPosts();

  return (
    <>
      <Section tone="paper" eager className="relative overflow-hidden">
        <Aurora variant="light" className="opacity-70" />
        <Container className="relative">
          <nav aria-label="Breadcrumb">
            <ol className="flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-eyebrow uppercase text-ink-500">
              <li>
                <Link
                  to="/"
                  className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-sm text-brand-700 transition-brand hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-700"
                >
                  Home
                </Link>
              </li>
              <li aria-hidden="true">/</li>
              <li aria-current="page">Articles</li>
            </ol>
          </nav>

          <div className="mt-8">
            <Eyebrow>Articles</Eyebrow>
            <h1
              className="mt-5 max-w-[20ch] font-display text-display-lg text-ink-900"
              style={{ textWrap: "balance" }}
            >
              Writing on AI for small businesses.
            </h1>
          </div>
          <p className="mt-6 max-w-[60ch] text-body-lg text-ink-600">
            Plain, specific, and free of hype. What we have learned finding where AI pays in small
            businesses, and where it does not.
          </p>
          <CategoryChips />
        </Container>
      </Section>

      <Section tone="raised">
        <Container>
          <PostList posts={posts} />
        </Container>
      </Section>
    </>
  );
}
